import type { AvailabilityEntry, DayKey } from '../availability';
import type { SearchDescriptor } from '../descriptor';
import type { Predicate } from '../predicate';
import { always, and, elemMatch, eq, never, pathCmp } from '../predicate';
import { eqStringLiteralWhenPresent, eqWhenPresent, groupedEqStringLiteralsWhenPresent } from './scopeFilters';

export interface ProductInventoryLevel {
  readonly available?: number;
  readonly reserved?: number;
}

type ProductInventory = Readonly<Record<number, Readonly<Record<string, ProductInventoryLevel>>>>;

export interface ProductDoc {
  readonly objectID: string;
  readonly productId: number;
  readonly organizationId: number;
  readonly description: string;
  readonly productCode: string;
  readonly disabled: boolean;
  readonly commodity?: string;
  readonly commodityLabel?: string;
  readonly commoditySize?: string;
  readonly commodityStyle?: string;
  readonly commodityUnit?: string;
  readonly organic?: boolean;
  readonly countryOfOrigin?: string;
  readonly variableWeight: boolean;
  readonly weightPerInventoryUnit: number;
  readonly cartonsPerPallet: number | null;
  readonly inventory: ProductInventory;
  readonly availability?: readonly AvailabilityEntry[];
}

export const PRODUCT_DESCRIPTOR: SearchDescriptor<ProductDoc> = {
  entity: 'product',
  indexName: { algolia: 'products', typesense: 'products' },
  searchable: ['description', 'productCode', 'commodity', 'organic'],
  filterable: ['availability', 'commodity', 'countryOfOrigin', 'disabled', 'inventory', 'organic', 'organizationId'],
  defaults: { hitsPerPage: 20 },
  algolia: {
    searchableAttributes: [
      { path: ['description'] },
      { path: ['productCode'], mode: 'unordered' },
      { path: ['commodity'], mode: 'unordered' },
      { path: ['organic'], mode: 'unordered' },
    ],
    attributesForFaceting: [
      { path: ['commodity'] },
      { path: ['countryOfOrigin'] },
      { path: ['disabled'], mode: 'filterOnly' },
      { path: ['organic'] },
      { path: ['organizationId'], mode: 'filterOnly' },
    ],
  },
  ranking: { typesense: { queryBy: 'description,productCode,commodity,commodityLabel,organicKeyword' } },
  tenantField: 'organizationId',
};

export const PRODUCT_ENABLED = (): Predicate<ProductDoc> => eq('disabled', false);

export const PRODUCT_WITH_INVENTORY = (scope: {
  readonly warehouseId: number;
  readonly isoDate: string | null;
}): Predicate<ProductDoc> => {
  if (scope.isoDate === null) return never();

  return and([
    PRODUCT_ENABLED(),
    pathCmp(['inventory', String(scope.warehouseId), scope.isoDate, 'available'], '>', 0),
  ]);
};

export const PRODUCT_AVAILABLE_IN = (warehouseId: number, day: DayKey): Predicate<ProductDoc> =>
  elemMatch<ProductDoc>('availability', {
    kind: 'and',
    operands: [
      { kind: 'eq', field: 'warehouseId', value: String(warehouseId) },
      { kind: 'eq', field: 'date', value: day },
      { kind: 'cmp', field: 'available', op: '>', value: 0 },
    ],
  });

export interface ProductProductionRunScope {
  readonly filterDisabled?: boolean | null;
  readonly commodityName?: string | null;
  readonly commodityNames?: readonly string[] | null;
  readonly countryOfOrigin?: string | null;
  readonly organic?: boolean | null;
}

export const PRODUCT_FOR_PRODUCTION_RUN = (scope: ProductProductionRunScope): Predicate<ProductDoc> =>
  and([
    scope.filterDisabled ? PRODUCT_ENABLED() : always(),
    eqStringLiteralWhenPresent<ProductDoc>('commodity', scope.commodityName),
    groupedEqStringLiteralsWhenPresent<ProductDoc>('commodity', scope.commodityNames),
    eqWhenPresent<ProductDoc>('countryOfOrigin', scope.countryOfOrigin),
    eqWhenPresent<ProductDoc>('organic', scope.organic),
  ]);
